import { Component, DestroyRef, inject } from '@angular/core';
import { DatePipe, UpperCasePipe } from '@angular/common';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { throwError } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { Globals } from '@core/globals';
import { ButtonComponent } from '@atoms/button/button.component';
import { CheckboxComponent } from '@atoms/checkbox/checkbox.component';
import { ColorTextComponent } from '@atoms/color-text/color-text.component';
import { FormComponent } from '@atoms/form/form.component';
import { FormInputsComponent } from '@atoms/form/form-inputs/form-inputs.component';
import { FormSectionComponent } from '@atoms/form/form-section/form-section.component';
import { FormTitleComponent } from '@atoms/form/form-title/form-title.component';
import { GridComponent } from '@atoms/grid/grid.component';
import { IconComponent } from '@atoms/icon/icon.component';
import { InputComponent } from '@atoms/input/input.component';
import { LinkComponent } from '@atoms/link/link.component';
import { ParagraphComponent } from '@atoms/paragraph/paragraph.component';
import { PhoneInputComponent } from '@atoms/phone-input/phone-input.component';
import { SelectComponent } from '@atoms/select/select.component';
import { SelectItemComponent } from '@atoms/select/select-item/select-item.component';
import { TextareaComponent } from '@atoms/textarea/textarea.component';
import { ContactInformationsComponent } from '@molecules/contact-informations/contact-informations.component';
import { FormThanksMessageComponent } from '@molecules/form-thanks-message/form-thanks-message.component';
import { ModalHeaderComponent } from '@molecules/modal/modal-header/modal-header.component';
import { ModalMainComponent } from '@molecules/modal/modal-main/modal-main.component';
import { ModalMainContentComponent } from '@molecules/modal/modal-main-content/modal-main-content.component';
import { ModalSidebarComponent } from '@molecules/modal/modal-sidebar/modal-sidebar.component';
import { ModalSidebarSectionComponent } from '@molecules/modal/modal-sidebar-section/modal-sidebar-section.component';
import { ModalTitleComponent } from '@molecules/modal/modal-title/modal-title.component';
import { MakeModelSingleComponent } from '@organisms/make-model-single/make-model-single.component';
import { FilterDataService } from '@services/filter-data.service';
import { AppointmentValuationRequest, AppointmentValuationService } from '@services/appointment-valuation.service';
import { GlobalsService } from '@services/globals.service';
import { MakeModelDropdownSingleOutput } from '@models/make-mode-dropdown.model';
import { GtmService } from '@services/gtm.service';

declare var Cookies: any;

@Component({
  selector: 'app-sell-car-modal',
  imports: [
    UpperCasePipe,
    ReactiveFormsModule,
    TranslateModule,
    ButtonComponent,
    CheckboxComponent,
    ColorTextComponent,
    FormComponent,
    FormInputsComponent,
    FormThanksMessageComponent,
    FormSectionComponent,
    FormTitleComponent,
    GridComponent,
    IconComponent,
    InputComponent,
    LinkComponent,
    ParagraphComponent,
    PhoneInputComponent,
    SelectComponent,
    SelectItemComponent,
    TextareaComponent,
    ContactInformationsComponent,
    ModalHeaderComponent,
    ModalMainComponent,
    ModalMainContentComponent,
    ModalSidebarComponent,
    ModalSidebarSectionComponent,
    ModalTitleComponent,
    MakeModelSingleComponent,
  ],
  providers: [DatePipe],
  templateUrl: './sell-car-modal.component.html',
  styleUrl: './sell-car-modal.component.scss',
})
export class SellCarModalComponent {
  private fb = inject(FormBuilder);
  private activatedRoute = inject(ActivatedRoute);
  private destroyRef = inject(DestroyRef);
  public globals = inject(Globals);
  private globalsService = inject(GlobalsService);
  public filterData = inject(FilterDataService);
  private appointmentValuationService = inject(AppointmentValuationService);
  private gtmService = inject(GtmService);
  private datePipe = inject(DatePipe);
  public form = this.fb.nonNullable.group({
    name: ['', { nonNullable: true, validators: [Validators.required] }],
    email: ['', { nonNullable: true, validators: [Validators.required, Validators.email] }],
    phone: ['', { nonNullable: true, validators: [Validators.required] }],
    phoneCode: ['+420', [Validators.required]],
    message: ['', { nonNullable: true, validators: [] }],
    date: ['', { nonNullable: true, validators: [Validators.required] }],
    time: ['', { nonNullable: true, validators: [Validators.required] }],
    sellCarMakeModel: [undefined as MakeModelDropdownSingleOutput | undefined, { nonNullable: true, validators: [Validators.required] }],
    sellCarMileage: ['', { nonNullable: true, validators: [Validators.required] }],
    sellCarYear: ['', { nonNullable: true, validators: [Validators.required] }],
    sellCarFuel: ['', { nonNullable: true, validators: [Validators.required] }],
    sellCarPrice: ['', { nonNullable: true, validators: [Validators.required] }],
    changeCar: [false, { nonNullable: true, validators: [] }],
    isCompany: [false, { nonNullable: true, validators: [] }],
    marketingAgree: [false, { nonNullable: true, validators: [] }],
  });
  private userIp: string = '';
  public timeOptions: string[] = [];
  public step = 1;

  send(): void {
    if (this.form.invalid) return;
    const formData = this.form.getRawValue();
    const utmSource: string | null = this.activatedRoute.snapshot.queryParamMap.get('utm_source');
    const utmMedium: string | null = this.activatedRoute.snapshot.queryParamMap.get('utm_medium');
    const utmCampaign: string | null = this.activatedRoute.snapshot.queryParamMap.get('utm_campaign');
    const utmContent: string | null = this.activatedRoute.snapshot.queryParamMap.get('utm_content');

    const statsMonitorId = Cookies.get('_stats_of_visits');
    const exponeaEtc = Cookies.get('__exponea_etc__');
    const gaClientId = Cookies.get('_ga');

    const unixTimestamp: number = Math.floor(Date.now());
    const randomNumber: number = Math.floor(Math.random() * (999 - 100 + 1) + 100); // 100 - 999
    const formUniqueId: string = unixTimestamp + '' + randomNumber + '10';

    const date: string = this.datePipe.transform(formData.date, 'yyyy-MM-dd') ?? '';
    const time: string = formData.time;

    let message = formData.message + `, Firma: ${formData.isCompany ? 'Ano' : 'Ne'}, požadovaná cena: ${formData.sellCarPrice} Kč, Mám zájem o výměnu vozu za jiné: ${formData.changeCar ? 'Ano' : 'Ne'}`;

    const request: AppointmentValuationRequest = {
      appointmentDateTime: date + ' ' + time + ':00',
      branchId: 356,
      buyPlanned: true,
      customerIP: this.userIp,
      dateTime: this.datePipe.transform(new Date(), 'yyyy-MM-dd HH:mm:ss') ?? '',
      dsData: {},
      email: formData.email,
      exponeaEtc: exponeaEtc ?? undefined,
      formUniqueId,
      fuelTypeId: +formData.sellCarFuel,
      gaClientId: gaClientId ?? '',
      makeId: formData.sellCarMakeModel!.makeId,
      marketingAgree: formData.marketingAgree,
      marketingAgreeEml: formData.marketingAgree,
      comment: message,
      modelId: formData.sellCarMakeModel!.modelId,
      name: formData.name,
      personalAgree: true,
      phone: formData.phoneCode + formData.phone,
      statsMonitorId: statsMonitorId ?? undefined,
      urmContent: utmContent ?? '',
      utmCampaign: utmCampaign ?? '',
      utmMedium: utmMedium ?? '',
      utmSource: utmSource ?? '',
      webFormUrl: 'https://www.mototechna.cz',
      webRefferal: `${window.location.href}`,
      year: +formData.sellCarYear,
    };
    this.appointmentValuationService
      .sendForm(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          if (response.error || (response.status && (response.status === 400 || response.status === 500))) return;
          this.form.reset();
          this.form.markAsUntouched();
          this.step = 2;

          const dataLayer = {
            event: 'page_view',
            pageURL: '/' + this.globals.language() + '/dekovacka-oceneni-vozu-na-pobocce',
            pageType: 'Purchase-4',
            pageTitle: 'Poděkování ocenění vozu na pobočce',
          };
          this.gtmService.pushGtm(dataLayer);
        },
        error: (error) => {
          throwError(() => 'Something went wrong:' + error);
        },
      });
  }

  ngOnInit(): void {
    this.timeOptions = this.globalsService.createTimeOptions();

    const dataLayer = {
      event: 'page_view',
      pageURL: '/' + this.globals.language() + '/oceneni-vozu-na-pobocce',
      pageType: 'Form-4',
      pageTitle: 'Ocenění vozu na pobočce',
    };
    this.gtmService.pushGtm(dataLayer);

    this.appointmentValuationService
      .getUserIp()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.userIp = response.ip;
        },
        error: () => {
          this.userIp = '108.121.121.211'; // Error set placeholder
        },
      });
  }
}
